<template>
  <div class="edit-user-guide">
    <div
      v-if="aclHelper.getSettingsUpdateAccess()"
      class="row edit-button-group"
    >
      <button
        v-if="!allowEdit"
        id="settings-general-document-admin-edit"
        class="btn btn--orange"
        @click="enableEdit()"
      >{{ $t('action.edit') }}</button>
      <button
        v-if="allowEdit"
        id="settings-general-document-admin-cancel"
        v-hkldreset
        class="btn btn--darkblue"
        @click="discardChange()"
      >{{ $t('action.cancel') }}</button>
      <button
        v-if="allowEdit"
        id="settings-general-document-admin-save"
        v-hkldreset
        class="btn btn--orange"
        @click="save()"
      >{{ $t('action.save') }}</button>
    </div>

    <div
      id="project-docs-attachments"
      class="edit-attachments"
    >
      <table
        v-show="files.length>0"
        class="tender-tnc-table"
      >
        <thead class="file-upload-th">
          <th class="number">
            #
          </th>
          <th class="filename">
            {{ $t('tender.attachment.document') }}
          </th>
          <th class="description ">
            {{ $t('tender.attachment.description') }}
          </th>
          <th
            v-if="enableTenancyRecordsFeature"
            class="docs-checkbox "
          >
            {{ $t('userGuideForms.tenant') }}
          </th>
          <th
            v-if="enableWorkPermitsFeature"
            class="docs-checkbox "
          >
            {{ $t('userGuideForms.contractor') }}
          </th>
          <th
            v-if="enableTenderFeature || enableProjectFeature"
            class="docs-checkbox "
          >
            {{ $t('userGuideForms.hklVendor') }}
          </th>
          <th class="docs-checkbox ">
            {{ $t('userGuideForms.hklUser') }}
          </th>
          <th>
            {{ $t('tender.attachment.effectiveFrom') }}
          </th>
          <th>
            {{ $t('tender.attachment.effectiveTo') }}
          </th>
          <th class="uploaded-date">
            {{ $t('tender.attachment.uploadedDate') }}
          </th>
          <th v-if="canDelete && allowEdit" />
        </thead>
        <draggable
          :options="{handle:'.myHandler'}"
          element="tbody"
          :list="pendingUpdateFiles"
        >
          <template v-for="(item, index) in pendingUpdateFiles">
            <tr
              :key="item.id"
              class="set-border-top"
              :class="{myHandler: allowEdit}"
            >
              <td class="number ">
                <span class="attachment-index">{{ index + 1 }}</span>
              </td>
              <td class="filename ">
                <a
                  style="text-decoration: underline;"
                  @click="downloadFile(item.id)"
                >
                  {{ item.filename }}
                </a>
              </td>
              <td class="description ">
                <div v-show="allowEdit">
                  <textarea
                    v-autosize
                    class="autosize file-upload-desc"
                    :value="item.description"
                    @change="event => pendingUpdateFiles[index].description = event.target.value"
                  />
                </div>
                <div v-show="!allowEdit">
                  <div
                    class="emphasis description-text"
                    v-html="item.description"
                  />
                </div>
              </td>
              <td
                v-if="enableTenancyRecordsFeature"
                class="docs-checkbox "
              >
                <input
                  type="checkbox"
                  :disabled="!allowEdit"
                  :checked="item.metaData.tenant === '1'"
                  @change="(e) => {item.metaData.tenant = e.target.checked ? '1' : '0'}"
                >
              </td>
              <td
                v-if="enableWorkPermitsFeature"
                class="docs-checkbox "
              >
                <input
                  type="checkbox"
                  :disabled="!allowEdit"
                  :checked="item.metaData.contractor === '1'"
                  @change="(e) => {item.metaData.contractor = e.target.checked ? '1' : '0'}"
                >
              </td>
              <td
                v-if="enableTenderFeature || enableProjectFeature"
                class="docs-checkbox "
              >
                <input
                  type="checkbox"
                  :disabled="!allowEdit"
                  :checked="item.metaData.hklVendor === '1'"
                  @change="(e) => {item.metaData.hklVendor = e.target.checked ? '1' : '0'}"
                >
              </td>
              <td class="docs-checkbox">
                <input
                  type="checkbox"
                  :disabled="!allowEdit"
                  :checked="item.metaData.hklUser === '1'"
                  @change="(e) => {item.metaData.hklUser = e.target.checked ? '1' : '0'}"
                >
              </td>
              <td class="effective-date">
                <DatePicker
                  :disabledPicker="!allowEdit || isDocEffective(item.effectiveDate)"
                  format="DD MMM YYYY"
                  :clearButton="allowEdit && !isDocEffective(item.effectiveDate)"
                  :class="{ 'error-highlight' : !isValidEffectiveFromAndTo && (!item.effectiveDate) && item.endDate }"
                  :disabled="disabledDatesForEffectiveDate(item)"
                  :calendarButton="true"
                  calendarButtonIcon="fa fa-calendar"
                  :value="getDayFromDate(item.effectiveDate)"
                  @selected="val => updateEffectiveDate(val, item)"
                />
              </td>
              <td class="effective-date">
                <DatePicker
                  :disabledPicker="!allowEdit"
                  format="DD MMM YYYY"
                  :clearButton="allowEdit"
                  :disabled="disabledDatesForEndDate(item)"
                  :calendarButton="true"
                  calendarButtonIcon="fa fa-calendar"
                  :value="getDayFromDate(item.endDate)"
                  @selected="val => updateEndDate(val, item)"
                />
              </td>
              <td
                class="uploaded-at"
                style="text-align: right;"
              >
                {{ formatDate(item.createdAt) }}
              </td>
              <td v-if="canDelete && allowEdit">
                <div
                  class="file-upload-delete"
                  @click="deleteFile(index, item)"
                >
                  &#x2715;
                </div>
              </td>
            </tr>
          </template>
        </draggable>
      </table>
      <div
        v-show="canDownloadAll && files.length > 0"
        class="projects-download-all clearfix"
      >
        <button
          class="btn btn--darkblue tender-sticky-button float-left"
          @click="downloadAll(sourceId)"
        >{{ $t('action.download') }} {{ $t('all') }}</button>
        <div class="float-right clearfix">
          <slot name="buttons" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EditForms from '@/controls/EditForms'
import { mapGetters } from 'vuex'

export default {
  name: 'EditUserGuide',
  mixins: [ EditForms ],
  computed: {
    ...mapGetters('regions', ['enableTenancyRecordsFeature', 'enableWorkPermitsFeature', 'enableTenderFeature', 'enableProjectFeature']),
  },
}
</script>

<style scoped lang="scss">
@import '~styles/tender/attachments';
</style>

<style lang="scss" scoped>
@import './EditGuideAndForms.scss';
.tender-tnc-table ::v-deep{
  .description {
    width: 50%;
  }
}
</style>
